import { useState } from 'react';
import type { ReactNode } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../auth/AuthContext';
import { Avatar, Button } from './ui';

interface NavEntry {
  to: string;
  label: string;
  icon: string;
  /** Key into the dashboard's queue counts, shown as a badge. */
  badge?: 'kycPending' | 'categoriesPending' | 'policeVerificationPending' | 'reportsOpen' | 'freePostsPending';
  /** Present in the nav but not navigable, with the reason on hover. */
  blocked?: string;
  /** The permission needed to see it. Without one, every admin does. */
  perm?: string;
  /** Shown to a super admin only. */
  superOnly?: boolean;
}

/**
 * The v2 §30 module list, grouped by what an operator is actually doing.
 *
 * Membership and profit sharing appear here deliberately, disabled, rather
 * than being quietly omitted: they are part of the requested product and
 * whoever opens this console should see that they are accounted for and why
 * they are not available.
 */
const NAV: Array<{ label: string; items: NavEntry[] }> = [
  {
    label: 'Overview',
    items: [
      { to: '/', label: 'Dashboard', icon: '◧', perm: 'dashboard.view' },
      { to: '/analytics', label: 'Analytics', icon: '◪', perm: 'dashboard.view' },
    ],
  },
  {
    label: 'Queues',
    items: [
      { to: '/kyc-management', label: 'KYC Management', icon: '⛨', perm: 'kyc.view' },
      { to: '/kyc', label: 'KYC review', icon: '⛉', badge: 'kycPending', perm: 'kyc.view' },
      { to: '/police-verifications', label: 'Police verification', icon: '⛨', badge: 'policeVerificationPending', perm: 'providers.view' },
      { to: '/moderation/categories', label: 'Category requests', icon: '⌸', badge: 'categoriesPending', perm: 'categories.view' },
      { to: '/reports', label: 'Reported content', icon: '⚑', badge: 'reportsOpen', perm: 'reports.view' },
    ],
  },
  {
    label: 'People',
    items: [
      { to: '/users', label: 'Users', icon: '☖', perm: 'users.view' },
      { to: '/providers', label: 'Service providers', icon: '⚒', perm: 'providers.view' },
      { to: '/agents', label: 'Partners', icon: '⚯', perm: 'agents.view' },
      { to: '/team', label: 'Partner teams', icon: '⛬', perm: 'agents.view' },
      { to: '/crm', label: 'CRM customers', icon: '☎', perm: 'crm.view' },
    ],
  },
  {
    label: 'Marketplace',
    items: [
      { to: '/listings', label: 'Listings', icon: '▤', perm: 'listings.view' },
      { to: '/properties', label: 'Properties & sold out', icon: '⌂', perm: 'listings.view' },
      { to: '/watermark', label: 'Photo watermark', icon: '◈', perm: 'settings.view' },
      { to: '/contact-visibility', label: 'Contact visibility', icon: '☎', perm: 'settings.view' },
      { to: '/provider-media', label: 'Provider photos & videos', icon: '▣', perm: 'providers.view' },
      { to: '/free-posts', label: 'Free Post Management', icon: '🎁', badge: 'freePostsPending', perm: 'listings.view' },
      { to: '/jobs', label: 'Jobs', icon: '⚒', perm: 'listings.view' },
      { to: '/bookings', label: 'Bookings', icon: '✆', perm: 'bookings.view' },
      { to: '/chat', label: 'Chat moderation', icon: '✉', perm: 'chat.view' },
      { to: '/ads', label: 'Advertisements', icon: '◫', perm: 'ads.view' },
    ],
  },
  {
    label: 'Commerce',
    items: [
      { to: '/payments', label: 'Payments', icon: '₹', perm: 'payments.view' },
      { to: '/gst', label: 'GST report', icon: '⎙', perm: 'payments.view' },
      { to: '/plans', label: 'Plans & pricing', icon: '⛁', perm: 'plans.view' },
      { to: '/communities', label: 'Communities', icon: '◎', perm: 'communities.view' },
      { to: '/free-slots', label: 'Free slots', icon: '◷', perm: 'plans.view' },
      { to: '/bonuses', label: 'Bonus ledger', icon: '⚖', perm: 'agents.view' },
      { to: '/gap-ids', label: 'Global Axis Partner', icon: '🆔', perm: 'agents.view' },
      { to: '/profit-sharing', label: 'Profit sharing', icon: '⛔', blocked: 'Blocked pending legal review — PRS v2 §0.2(a)' },
    ],
  },
  {
    label: 'Content',
    items: [
      { to: '/categories', label: 'Categories', icon: '⌗', perm: 'categories.view' },
      { to: '/filters', label: 'Filters', icon: '⚙', perm: 'categories.view' },
      { to: '/locations', label: 'Locations', icon: '⌘', perm: 'locations.view' },
      { to: '/channels', label: 'Channels', icon: '⌬', perm: 'categories.view' },
    ],
  },
  {
    label: 'System',
    items: [
      { to: '/notifications', label: 'Notifications', icon: '🔔', perm: 'notifications.view' },
      { to: '/settings', label: 'Settings', icon: '⚙', perm: 'settings.view' },
      { to: '/security', label: 'Security', icon: '⛨', perm: 'security.view' },
      { to: '/login-history', label: 'Login history', icon: '⏻', perm: 'security.view' },
      { to: '/audit', label: 'Audit trail', icon: '⌚', perm: 'audit.view' },
    ],
  },
];

interface DashboardCounts {
  queues: Record<string, number>;
}

export function Shell() {
  const { user, access, can, signOut } = useAuth();
  // What this admin is not allowed to open is not offered. The server refuses
  // it either way; this only spares them a menu of dead ends.
  const visible = (item: NavEntry) =>
    item.superOnly ? Boolean(access?.isSuperAdmin) : !item.perm || can(item.perm);
  const nav = NAV.map((g) => ({ ...g, items: g.items.filter(visible) })).filter((g) => g.items.length > 0);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Queue depths refresh on their own so a badge does not go stale while an
  // operator sits on one screen working through a list.
  const { data } = useQuery({
    queryKey: ['dashboard-badges'],
    queryFn: () => api.get<DashboardCounts>('/admin/dashboard'),
    // The counts come from the dashboard, which a role may not include.
    enabled: can('dashboard.view'),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const title =
    NAV.flatMap((g) => g.items).find((i) => i.to === location.pathname)?.label ??
    NAV.flatMap((g) => g.items).find((i) => i.to !== '/' && location.pathname.startsWith(i.to))?.label ??
    'Admin';

  return (
    <div className="shell">
      {open && <div className="scrim" onClick={() => setOpen(false)} />}

      <aside className={`sidebar ${open ? 'sidebar--open' : ''}`.trim()}>
        <div className="sidebar__brand">
          <img src="/logo.png" alt="" />
          <div className="sidebar__brand-text">
            <span className="sidebar__brand-name">ISawBazaar</span>
            <span className="sidebar__brand-sub">Admin</span>
          </div>
        </div>
        <div className="sidebar__rule" />

        <nav className="sidebar__nav">
          {nav.map((group) => (
            <div className="nav-group" key={group.label}>
              <div className="nav-group__label">{group.label}</div>
              {group.items.map((item) =>
                item.blocked ? (
                  <div key={item.to} className="nav-item nav-item--blocked" title={item.blocked}>
                    <span className="nav-item__icon">{item.icon}</span>
                    <span>{item.label}</span>
                  </div>
                ) : (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/'}
                    className={({ isActive }) => `nav-item ${isActive ? 'nav-item--active' : ''}`}
                    onClick={() => setOpen(false)}
                  >
                    <span className="nav-item__icon">{item.icon}</span>
                    <span>{item.label}</span>
                    {item.badge && (data?.queues?.[item.badge] ?? 0) > 0 && (
                      <span className="nav-item__badge">{data?.queues?.[item.badge]}</span>
                    )}
                  </NavLink>
                ),
              )}
            </div>
          ))}
        </nav>
      </aside>

      <div className="main">
        <header className="topbar">
          <button className="btn btn--ghost btn--sm menu-toggle" onClick={() => setOpen((v) => !v)} aria-label="Menu">
            ☰
          </button>
          <div>
            <div className="topbar__title">{title}</div>
          </div>
          <div className="topbar__spacer" />
          <div className="row">
            <Avatar name={user?.displayName ?? 'Admin'} />
            <div style={{ lineHeight: 1.25 }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>{user?.displayName ?? 'Administrator'}</div>
              <div style={{ fontSize: 11.5, color: 'var(--ink-muted)' }}>{user?.mobile}</div>
            </div>
            <Button size="sm" onClick={signOut}>
              Sign out
            </Button>
          </div>
        </header>

        <main className="content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export function PageIntro({ children }: { children: ReactNode }) {
  return <div style={{ marginBottom: 'var(--s4)' }}>{children}</div>;
}
